import React, { useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  ProgressBar,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";

import Layout from "../../layout/Layout";
import {
  exportDomesticPurchaseXmlsApi,
  previewDomesticPurchaseXmlsApi,
} from "../../../api/muhasebe/domesticPurchaseXmlApi";

const MAX_FILES = 30;

const INITIAL_OPTIONS = {
  companyName: "",
  diibInfo: "",
  expiryDate: "",
  materialName: "",
};

const getErrorMessage = async (error) => {
  const responseData = error?.response?.data;

  if (responseData instanceof Blob) {
    try {
      const parsed = JSON.parse(await responseData.text());
      return parsed?.message || parsed?.msg || "İşlem tamamlanamadı.";
    } catch {
      return "İşlem tamamlanamadı.";
    }
  }

  return (
    responseData?.message ||
    responseData?.msg ||
    error?.message ||
    "İşlem sırasında beklenmeyen bir hata oluştu."
  );
};

const formatNumber = (value, digits = 2) => {
  if (value === null || value === undefined || value === "") return "-";
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);

  return new Intl.NumberFormat("tr-TR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  }).format(number);
};

const formatFileSize = (size) => {
  const kb = Number(size || 0) / 1024;
  return kb >= 1024
    ? `${formatNumber(kb / 1024, 2)} MB`
    : `${formatNumber(kb, 1)} KB`;
};

const statusBadge = (status) => {
  if (status === "success") return <Badge bg="success">Okundu</Badge>;
  if (status === "warning") return <Badge bg="warning" text="dark">Uyarı</Badge>;
  if (status === "duplicate") return <Badge bg="secondary">Mükerrer</Badge>;
  return <Badge bg="danger">Hata</Badge>;
};

function DomesticPurchaseXmlPage() {
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [options, setOptions] = useState(INITIAL_OPTIONS);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState(0);

  const resetResult = () => {
    setPreview(null);
    setError("");
    setProgress(0);
  };

  const handleFilesSelected = (event) => {
    const selected = Array.from(event.target.files || []);
    const invalidFiles = selected.filter(
      (file) => !file.name.toLocaleLowerCase("tr-TR").endsWith(".xml")
    );

    if (invalidFiles.length) {
      setError(
        `Yalnızca XML seçilebilir: ${invalidFiles
          .map((file) => file.name)
          .join(", ")}`
      );
      setFiles([]);
      setPreview(null);
      return;
    }

    if (selected.length > MAX_FILES) {
      setError(`Tek işlemde en fazla ${MAX_FILES} XML seçilebilir.`);
      setFiles([]);
      setPreview(null);
      return;
    }

    setFiles(selected);
    resetResult();
  };

  const removeFile = (index) => {
    setFiles((current) => current.filter((_, fileIndex) => fileIndex !== index));
    resetResult();
  };

  const moveFile = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= files.length) return;

    const reordered = [...files];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setFiles(reordered);
    resetResult();
  };

  const clearFiles = () => {
    setFiles([]);
    setPreview(null);
    setError("");
    setProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleOptionChange = (event) => {
    const { name, value } = event.target;
    setOptions((current) => ({ ...current, [name]: value }));
  };

  const handleProgress = (event) => {
    if (!event.total) return;
    setProgress(Math.min(100, Math.round((event.loaded * 100) / event.total)));
  };

  const analyzeFiles = async () => {
    if (!files.length) {
      setError("Önce en az bir XML faturası seçin.");
      return;
    }

    setIsPreviewing(true);
    setError("");
    setProgress(0);

    try {
      const response = await previewDomesticPurchaseXmlsApi(
        files,
        options,
        handleProgress
      );
      setPreview(response?.data?.data || null);
      setProgress(100);
    } catch (requestError) {
      setPreview(requestError?.response?.data?.data || null);
      setError(await getErrorMessage(requestError));
    } finally {
      setIsPreviewing(false);
    }
  };

  const downloadExcel = async () => {
    if (!files.length) {
      setError("Önce en az bir XML faturası seçin.");
      return;
    }

    setIsExporting(true);
    setError("");
    setProgress(0);

    try {
      const response = await exportDomesticPurchaseXmlsApi(
        files,
        options,
        handleProgress
      );
      const disposition = response.headers?.["content-disposition"] || "";
      const fileNameMatch = disposition.match(/filename="?([^";]+)"?/i);
      const fileName = fileNameMatch?.[1] || "yurt-ici-alimlar.xlsx";
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");

      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setProgress(100);
    } catch (requestError) {
      setError(await getErrorMessage(requestError));
    } finally {
      setIsExporting(false);
    }
  };

  const summary = preview?.summary;
  const isBusy = isPreviewing || isExporting;

  return (
    <Layout>
      <div className="container-fluid px-0">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
          <div>
            <h2 className="fw-bold mb-1">Yurt İçi Alım XML Aktarımı</h2>
            <div className="text-muted">
              XML faturalarını seçildiği sırayla okuyup örnek formata uygun Excel oluşturur.
            </div>
          </div>
          <Badge bg="primary" className="fs-6 px-3 py-2">
            Yalnızca XML
          </Badge>
        </div>

        {error ? (
          <Alert variant="danger" dismissible onClose={() => setError("")}>
            {error}
          </Alert>
        ) : null}

        <Row className="g-4">
          <Col xl={7}>
            <Card className="shadow-sm border-0 h-100">
              <Card.Header className="bg-white fw-semibold py-3">
                1. XML faturalarını seçin
              </Card.Header>
              <Card.Body>
                <Form.Control
                  ref={fileInputRef}
                  type="file"
                  accept=".xml,application/xml,text/xml"
                  multiple
                  disabled={isBusy}
                  onChange={handleFilesSelected}
                />
                <div className="small text-muted mt-2">
                  Tek işlemde en fazla {MAX_FILES} dosya. Liste sırası Excel satır sırasıdır.
                </div>

                {files.length ? (
                  <div className="mt-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="fw-semibold">Seçilen dosyalar ({files.length})</span>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        disabled={isBusy}
                        onClick={clearFiles}
                      >
                        Listeyi temizle
                      </Button>
                    </div>

                    <div
                      className="border rounded"
                      style={{ maxHeight: 360, overflowY: "auto" }}
                    >
                      {files.map((file, index) => (
                        <div
                          key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
                          className="d-flex align-items-center gap-2 px-3 py-2 border-bottom"
                        >
                          <Badge bg="light" text="dark" className="flex-shrink-0">
                            {index + 1}
                          </Badge>
                          <div className="flex-grow-1 overflow-hidden">
                            <div className="fw-semibold text-truncate">{file.name}</div>
                            <div className="small text-muted">
                              {formatFileSize(file.size)}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            disabled={isBusy || index === 0}
                            title="Yukarı taşı"
                            onClick={() => moveFile(index, -1)}
                          >
                            ↑
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            disabled={isBusy || index === files.length - 1}
                            title="Aşağı taşı"
                            onClick={() => moveFile(index, 1)}
                          >
                            ↓
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            disabled={isBusy}
                            onClick={() => removeFile(index)}
                          >
                            Kaldır
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Alert variant="light" className="border mt-4 mb-0">
                    Henüz XML seçilmedi.
                  </Alert>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col xl={5}>
            <Card className="shadow-sm border-0 h-100">
              <Card.Header className="bg-white fw-semibold py-3">
                2. Excel üst bilgileri
              </Card.Header>
              <Card.Body>
                <Alert variant="info" className="small">
                  Bu alanlar boş kalırsa firma, DİİB, satır kodu, GTİP ve madde adı XML’den alınır.
                </Alert>
                <Form.Group className="mb-3">
                  <Form.Label>Firma adı (opsiyonel)</Form.Label>
                  <Form.Control
                    name="companyName"
                    value={options.companyName}
                    disabled={isBusy}
                    placeholder="XML’deki alıcı firma kullanılır"
                    onChange={handleOptionChange}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>DİİB tarih ve no (opsiyonel)</Form.Label>
                  <Form.Control
                    name="diibInfo"
                    value={options.diibInfo}
                    disabled={isBusy}
                    placeholder="Örn: 26.09.2025 2025/D1-04997"
                    onChange={handleOptionChange}
                  />
                </Form.Group>
                <Row className="g-3">
                  <Col md={6} xl={12} xxl={6}>
                    <Form.Group>
                      <Form.Label>Süre sonu (opsiyonel)</Form.Label>
                      <Form.Control
                        type="date"
                        name="expiryDate"
                        value={options.expiryDate}
                        disabled={isBusy}
                        onChange={handleOptionChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6} xl={12} xxl={6}>
                    <Form.Group>
                      <Form.Label>Madde adı (opsiyonel)</Form.Label>
                      <Form.Control
                        name="materialName"
                        value={options.materialName}
                        disabled={isBusy}
                        placeholder="Örn: ÇİNKO KAPLI SAC"
                        onChange={handleOptionChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card className="shadow-sm border-0 mt-4">
          <Card.Body>
            {isBusy || progress > 0 ? (
              <ProgressBar
                className="mb-3"
                now={progress}
                label={`${progress}%`}
                animated={isBusy}
              />
            ) : null}

            <div className="d-flex flex-wrap gap-2">
              <Button
                variant="outline-primary"
                disabled={isBusy || !files.length}
                onClick={analyzeFiles}
              >
                {isPreviewing ? (
                  <>
                    <Spinner size="sm" className="me-2" /> XML’ler okunuyor
                  </>
                ) : (
                  "XML’leri Oku ve Kontrol Et"
                )}
              </Button>
              <Button
                variant="success"
                disabled={isBusy || !preview?.rows?.length}
                onClick={downloadExcel}
              >
                {isExporting ? (
                  <>
                    <Spinner size="sm" className="me-2" /> Excel hazırlanıyor
                  </>
                ) : (
                  "Excel’i İndir"
                )}
              </Button>
            </div>
          </Card.Body>
        </Card>

        {preview ? (
          <>
            <Row className="g-3 mt-1">
              <Col sm={6} lg={3}>
                <Card className="border-0 shadow-sm h-100">
                  <Card.Body>
                    <div className="small text-muted">Yüklenen XML</div>
                    <div className="fs-3 fw-bold">{summary?.uploadedFileCount || 0}</div>
                  </Card.Body>
                </Card>
              </Col>
              <Col sm={6} lg={3}>
                <Card className="border-0 shadow-sm h-100">
                  <Card.Body>
                    <div className="small text-muted">Başarılı XML</div>
                    <div className="fs-3 fw-bold text-success">
                      {summary?.processedFileCount || 0}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col sm={6} lg={3}>
                <Card className="border-0 shadow-sm h-100">
                  <Card.Body>
                    <div className="small text-muted">Excel satırı</div>
                    <div className="fs-3 fw-bold">{summary?.rowCount || 0}</div>
                  </Card.Body>
                </Card>
              </Col>
              <Col sm={6} lg={3}>
                <Card className="border-0 shadow-sm h-100">
                  <Card.Body>
                    <div className="small text-muted">Hata / mükerrer</div>
                    <div className="fs-3 fw-bold text-danger">
                      {(summary?.errorCount || 0) + (summary?.duplicateCount || 0)}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Card className="shadow-sm border-0 mt-4">
              <Card.Header className="bg-white fw-semibold py-3">
                Dosya okuma sonuçları
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <Table hover className="mb-0 align-middle">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: 70 }}>Sıra</th>
                        <th>Dosya</th>
                        <th>Fatura No</th>
                        <th>Durum</th>
                        <th>Açıklama</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(preview.fileResults || []).map((result, index) => (
                        <tr key={`${result.originalName}-${index}`}>
                          <td>{index + 1}</td>
                          <td className="text-break">{result.originalName}</td>
                          <td>{result.invoiceNumber || "-"}</td>
                          <td>{statusBadge(result.status)}</td>
                          <td>
                            {result.message ||
                              (result.warnings || []).join(" ") ||
                              `${result.rowCount || 0} satır hazırlandı.`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>

            {preview.rows?.length ? (
              <Card className="shadow-sm border-0 mt-4">
                <Card.Header className="bg-white fw-semibold py-3">
                  Excel önizlemesi
                </Card.Header>
                <Card.Body className="p-0">
                  <div className="table-responsive" style={{ maxHeight: 500 }}>
                    <Table bordered hover size="sm" className="mb-0 align-middle">
                      <thead className="table-light position-sticky top-0">
                        <tr>
                          <th>Sıra</th>
                          <th>Satır Kodu</th>
                          <th>GTIP No</th>
                          <th>Madde Adı</th>
                          <th>Miktar (kg)</th>
                          <th>Net Tutar</th>
                          <th>Mal Bedeli (TL)</th>
                          <th>KDV (TL)</th>
                          <th>Toplam (TL)</th>
                          <th>Fatura Tarihi</th>
                          <th>Fatura No</th>
                          <th>Firma Adı</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.rows.map((row, index) => (
                          <tr key={`${row.invoiceNumber}-${row.lineCode}-${index}`}>
                            <td>{index + 1}</td>
                            <td>{row.lineCode || "-"}</td>
                            <td>{row.gtip || "-"}</td>
                            <td>{options.materialName || row.materialName || "-"}</td>
                            <td className="text-end">{formatNumber(row.quantity, 3)}</td>
                            <td className="text-end">
                              {formatNumber(row.netAmount)} {row.currency}
                            </td>
                            <td className="text-end">{formatNumber(row.netAmountTl)}</td>
                            <td className="text-end">{formatNumber(row.vatAmountTl)}</td>
                            <td className="text-end">{formatNumber(row.totalAmountTl)}</td>
                            <td>{row.invoiceDate || "-"}</td>
                            <td>{row.invoiceNumber || "-"}</td>
                            <td>{row.supplierName || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            ) : null}
          </>
        ) : null}
      </div>
    </Layout>
  );
}

export default DomesticPurchaseXmlPage;
